import { VideoCompressor } from './VideoCompressor';
import { ImageOptimizer } from './ImageOptimizer';
import { BatchProcessingOptions, CompressionResult, VideoCompressionOptions, ImageOptimizationOptions } from './types';

export class BatchProcessor {
  private options: BatchProcessingOptions;
  private videoCompressor?: VideoCompressor;
  private imageOptimizer?: ImageOptimizer;

  constructor(options: BatchProcessingOptions = {}) {
    this.options = {
      concurrency: 2,
      ...options
    };
  }

  async compressFiles(
    files: File[], 
    videoOptions?: VideoCompressionOptions,
    imageOptions?: ImageOptimizationOptions
  ): Promise<CompressionResult[]> {
    const results: CompressionResult[] = [];
    const errors: Array<{ file: File; error: Error; index: number }> = [];

    // Initialize compressors with options
    this.videoCompressor = new VideoCompressor(videoOptions);
    this.imageOptimizer = new ImageOptimizer(imageOptions);

    // Process files in batches based on concurrency
    const concurrency = this.options.concurrency || 2;
    const batches = this.createBatches(files, concurrency);

    for (const batch of batches) {
      const batchPromises = batch.map(async ({ file, index }) => {
        try {
          this.options.onFileStart?.(file, index);
          
          const result = await this.processFile(file);
          results[index] = result;
          
          this.options.onFileComplete?.(file, result, index);
          return result;
        } catch (error) {
          const err = error as Error;
          errors.push({ file, error: err, index });
          this.options.onFileError?.(file, err, index);
          throw error;
        }
      });

      // Wait for current batch to complete before processing next batch
      await Promise.allSettled(batchPromises);
    }

    // Clean up compressors
    await this.cleanup();

    // Call completion callback
    this.options.onAllComplete?.(results.filter(Boolean));

    return results.filter(Boolean);
  }

  private async processFile(file: File): Promise<CompressionResult> {
    const fileType = file.type.toLowerCase();

    if (fileType.startsWith('video/')) {
      if (!this.videoCompressor) {
        throw new Error('Video compressor not initialized');
      }
      return await this.videoCompressor.compress(file);
    } else if (fileType.startsWith('image/')) {
      if (!this.imageOptimizer) {
        throw new Error('Image optimizer not initialized');
      }
      return await this.imageOptimizer.process(file);
    } else {
      throw new Error(`Unsupported file type: ${fileType}`);
    }
  }

  private createBatches<T>(items: T[], batchSize: number): Array<Array<{ file: T; index: number }>> {
    const batches: Array<Array<{ file: T; index: number }>> = [];
    
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize).map((file, batchIndex) => ({
        file,
        index: i + batchIndex
      }));
      batches.push(batch);
    }
    
    return batches;
  }

  private async cleanup(): Promise<void> {
    try {
      if (this.videoCompressor) {
        await this.videoCompressor.terminate();
      }
    } catch (error) {
      console.warn('Error cleaning up video compressor:', error);
    }
  }

  // Helper method to process only video files
  async compressVideos(
    files: File[], 
    options?: VideoCompressionOptions
  ): Promise<CompressionResult[]> {
    const videoFiles = files.filter(file => file.type.startsWith('video/'));
    return this.compressFiles(videoFiles, options);
  }

  // Helper method to process only image files
  async optimizeImages(
    files: File[], 
    options?: ImageOptimizationOptions
  ): Promise<CompressionResult[]> {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    return this.compressFiles(imageFiles, undefined, options);
  }

  // Static method to analyze files and return statistics
  static analyzeFiles(files: File[]): {
    totalSize: number;
    videoCount: number;
    imageCount: number;
    otherCount: number;
    types: Record<string, number>;
  } {
    let totalSize = 0;
    let videoCount = 0;
    let imageCount = 0;
    let otherCount = 0;
    const types: Record<string, number> = {};

    files.forEach(file => {
      totalSize += file.size;
      
      const fileType = file.type.toLowerCase();
      types[fileType] = (types[fileType] || 0) + 1;

      if (fileType.startsWith('video/')) {
        videoCount++;
      } else if (fileType.startsWith('image/')) {
        imageCount++;
      } else {
        otherCount++;
      }
    });

    return {
      totalSize,
      videoCount,
      imageCount,
      otherCount,
      types
    };
  }
}
